import { useEffect, useState } from 'react'
import { nanoid } from 'nanoid'
import { EventBus, EventBusListenerFunction } from '../events'
import { useEventBusContext, useHostContext } from './context'

/**
 * Common Event Bus handling shared by the Event Bus hooks.
 */
const useEventBusAdapter = <EventType>(
  eventBus: EventBus<EventType>,
  listener?: EventBusListenerFunction<EventType>,
) => {
  // id is used as the unique reference to this "broker" so that we can ensure
  // listeners are reliably removed
  const [id] = useState(nanoid())

  useEffect(() => {
    // We try to remove the listener every time this hook evaluates
    eventBus.removeListener(id)
    if (listener) {
      eventBus.addListener({ id, listener })
    }
  }, [eventBus, listener, id])

  useEffect(
    () => () => {
      if (!eventBus) return
      eventBus.removeListener(id)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This effect is deliberately detached from it's dependencies. It should *only* run on hook destroy
    [],
  )
}

/**
 * A hook to interface with the Horizon Event Bus. Returns the closest Event Bus
 * instance from context.
 *
 * Optionally accepts a listener function which will be called when an event is
 * emitted.
 *
 * To improve performance, listeners should be wrapped in a `useCallback` or
 * defined as a static function outside the component, although logically this
 * hook will still work as intended without doing so. You may start to
 * experience a slowdown when many components with eventbus listeners are on
 * page together. Passing a standard function will mean that the signature
 * changes every render cycle, causing the listener to be removed and re-added.
 *
 * @example
 * const eventBus = useEventBus<EventType>(useCallback(event => {
 *   switch (event.topic) {
 *     case ...
 *   }
 * }), [deps])
 *
 * const handleClick = () => {
 *   eventBus.emit({ topic: ... })
 * }
 */
export function useEventBus<EventType>(
  listener?: EventBusListenerFunction<EventType>,
): EventBus<EventType> {
  const eventBus = useEventBusContext<EventType>()

  useEventBusAdapter(eventBus, listener)

  return eventBus
}

/**
 * A hook to interface with the Horizon Global Event Bus instead of the closest
 * instance. Returns the Global Event Bus instance.
 */
export function useGlobalEventBus<EventType>(
  listener?: EventBusListenerFunction<EventType>,
): EventBus<EventType> {
  const host = useHostContext()
  const eventBus = host.globalEventBus as EventBus<EventType>

  useEventBusAdapter(eventBus, listener)

  return eventBus
}
